import { DynamicStructuredTool, StructuredToolInterface } from '@langchain/core/tools';
import type { RunnableConfig } from '@langchain/core/runnables';
import { AIMessage, ToolCall } from '@langchain/core/messages';
import { z } from 'zod';
import { callLlm } from '../../model/llm.js';
import { getCurrentDate } from '../../agent/prompts.js';
import { formatToolResult } from '../types.js';
import { withTimeout, SUB_TOOL_TIMEOUT_MS, TTL_15M, TTL_1H, TTL_6H } from './utils.js';
import { drishtiApi } from './drishti-client.js';
import { drishtiGetWithFallback } from './mcp-fallback.js';
import type { components } from './drishti-openapi.js';

type Schema<K extends keyof components['schemas']> = components['schemas'][K];
type Paginated<T> = { data: T[]; has_next: boolean };

const SymbolsSchema = z.array(z.string()).max(20).optional();

const DateRangeSchema = {
  from: z.string().optional().describe('Inclusive ISO date or datetime lower bound.'),
  to: z.string().optional().describe('Inclusive ISO date or datetime upper bound.'),
};

const PagingSchema = {
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(50).default(10),
};

type EvidenceResult<T> = {
  data: T | null;
  url?: string;
  error?: string;
};

function uppercaseSymbols(symbols?: string[]): string[] | undefined {
  return symbols?.map((symbol) => symbol.trim().toUpperCase()).filter(Boolean);
}

function uppercaseSymbol(symbol: string): string {
  return symbol.trim().toUpperCase();
}

function daysAgoIso(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString();
}

async function evidenceGet<T>(
  endpoint: string,
  params: Record<string, string | number | boolean | string[] | undefined>,
  options: { cacheable?: boolean; ttlMs?: number } = {},
): Promise<EvidenceResult<T>> {
  try {
    // REST-first with automatic MCP fallback on rate-limit/quota errors.
    return await drishtiGetWithFallback<T>(endpoint, params, options);
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

async function evidencePost<T>(
  endpoint: string,
  body: unknown,
): Promise<EvidenceResult<T>> {
  try {
    return await drishtiApi.post<T>(endpoint, body);
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

function evidenceUrls(results: Array<EvidenceResult<unknown>>, extra: unknown[] = []): string[] {
  return [
    ...new Set([
      ...results.map((result) => result.url).filter((url): url is string => Boolean(url)),
      ...extra.flatMap(extractUrls),
    ]),
  ];
}

function extractUrls(value: unknown): string[] {
  const urls = new Set<string>();
  const urlKeys = new Set(['url', 'link', 'transcript_url', 'audio_url', 'intimation_attachment']);

  function walk(node: unknown): void {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== 'object') return;

    for (const [key, child] of Object.entries(node)) {
      if (typeof child === 'string' && urlKeys.has(key) && /^https?:\/\//.test(child)) {
        urls.add(child);
      } else {
        walk(child);
      }
    }
  }

  walk(value);
  return [...urls];
}

async function invokeToolCalls(toolCalls: ToolCall[], toolMap: Map<string, StructuredToolInterface>) {
  const results = await Promise.all(
    toolCalls.map(async (tc) => {
      try {
        const tool = toolMap.get(tc.name);
        if (!tool) throw new Error(`Tool '${tc.name}' not found`);

        const rawResult = await withTimeout(tool.invoke(tc.args), SUB_TOOL_TIMEOUT_MS, tc.name);
        const parsed = JSON.parse(typeof rawResult === 'string' ? rawResult : JSON.stringify(rawResult)) as {
          data?: unknown;
          sourceUrls?: string[];
        };

        return {
          tool: tc.name,
          args: tc.args,
          data: parsed.data,
          sourceUrls: parsed.sourceUrls ?? [],
          error: null,
        };
      } catch (error) {
        return {
          tool: tc.name,
          args: tc.args,
          data: null,
          sourceUrls: [],
          error: error instanceof Error ? error.message : String(error),
        };
      }
    }),
  );

  const combinedData: Record<string, unknown> = {};
  for (const result of results) {
    const symbol = (result.args as Record<string, unknown>).symbol;
    const symbols = (result.args as Record<string, unknown>).symbols;
    const keySuffix = typeof symbol === 'string'
      ? symbol
      : Array.isArray(symbols) && symbols.length > 0
        ? symbols.join('_')
        : undefined;
    const key = keySuffix ? `${result.tool}_${keySuffix}` : result.tool;
    combinedData[key] = result.error ? { error: result.error, args: result.args } : result.data;
  }

  return formatToolResult(combinedData, results.flatMap((result) => result.sourceUrls));
}

const SymbolLookupInputSchema = z.object({
  symbols: SymbolsSchema.describe('Indian listed symbols, e.g. RELIANCE,TCS. Maximum 20.'),
  scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes. Maximum 20.'),
});

export const getSymbolMetadata = new DynamicStructuredTool({
  name: 'drishti_get_symbol_metadata',
  description: 'Fetch Drishti symbol metadata: company name, market cap, sector, industry, theme, logo, and scrip code.',
  schema: SymbolLookupInputSchema,
  func: async (input) => {
    const params = {
      symbols: uppercaseSymbols(input.symbols),
      scrip_codes: input.scrip_codes,
    };
    const { data, url } = await drishtiGetWithFallback<Schema<'SymbolMetadataResponse'>>(
      '/v1/symbols/metadata',
      params,
      { cacheable: true, ttlMs: TTL_6H },
    );
    return formatToolResult(data, [url]);
  },
});

const NewsInputSchema = z.object({
  symbols: SymbolsSchema.describe('Optional Indian listed symbols. Omit for broad market news.'),
  scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes.'),
  sentiment: z.enum(['positive', 'negative', 'neutral']).optional(),
  ...DateRangeSchema,
  ...PagingSchema,
});

export const getNews = new DynamicStructuredTool({
  name: 'drishti_get_news',
  description: 'List Drishti news feed items with title, summary, company, symbol, sentiment, source, link, and date.',
  schema: NewsInputSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'NewsItem'>>>(
      '/v1/news',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_15M },
    );
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

const AnnouncementInputSchema = z.object({
  symbols: SymbolsSchema.describe('Optional Indian listed symbols. Omit for market-wide filings.'),
  scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes.'),
  categories: SymbolsSchema.describe('Optional Drishti announcement categories.'),
  important: z.boolean().default(false),
  detailed: z.boolean().default(false),
  ...DateRangeSchema,
  ...PagingSchema,
});

export const getAnnouncements = new DynamicStructuredTool({
  name: 'drishti_get_announcements',
  description: 'List Indian exchange announcements and corporate filings, filtered by symbols, categories, date range, importance, and detailed mode.',
  schema: AnnouncementInputSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Schema<'AnnouncementPaginatedResponse'>>(
      '/v1/announcements',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_15M },
    );
    return formatToolResult(data, [url]);
  },
});

export const getAnnouncementCategories = new DynamicStructuredTool({
  name: 'drishti_get_announcement_categories',
  description: 'Fetch Drishti corporate announcement categories grouped by important and not_important.',
  schema: z.object({}),
  func: async () => {
    const { data, url } = await drishtiGetWithFallback<Schema<'AnnouncementCategoriesResponse'>>(
      '/v1/announcements/categories',
      {},
      { cacheable: true, ttlMs: TTL_6H },
    );
    return formatToolResult(data, [url]);
  },
});

const AttachmentInputSchema = z.object({
  ids: z.array(z.string()).min(1).max(20).describe('Announcement or earnings ids to resolve attachment URLs for.'),
});

export const getAnnouncementAttachments = new DynamicStructuredTool({
  name: 'drishti_get_announcement_attachments',
  description: 'Resolve attachment URLs for specific Drishti announcement ids. Use only when a filing document is needed.',
  schema: AttachmentInputSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Schema<'BatchAttachmentLookupResponse'>>(
      '/v1/announcements/attachments',
      { ids: input.ids },
    );
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

const EarningsInputSchema = z.object({
  symbols: SymbolsSchema.describe('Optional Indian listed symbols. Omit for latest earnings feed.'),
  scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes.'),
  quarter: z.string().optional().describe('Fiscal quarter key such as q4_26.'),
  detailed: z.boolean().default(true),
  ...DateRangeSchema,
  ...PagingSchema,
});

export const getEarnings = new DynamicStructuredTool({
  name: 'drishti_get_earnings',
  description: 'List Drishti earnings filings with summaries and optional structured earnings tables.',
  schema: EarningsInputSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>(
      '/v1/earnings',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_1H },
    );
    return formatToolResult(data, [url]);
  },
});

export const getEarningsIndex = new DynamicStructuredTool({
  name: 'drishti_get_earnings_index',
  description: 'List lightweight Drishti earnings index rows with symbol, quarter, and filing date. Use for broad result-season scans before opening full earnings records.',
  schema: EarningsInputSchema.omit({ detailed: true }),
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'LightweightIndexItem'>>>(
      '/v1/earnings/index',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_1H },
    );
    return formatToolResult(data, [url]);
  },
});

const QuarterDetailSchema = z.object({
  symbol: z.string().optional().describe('Indian listed symbol. Do not pass with scrip_code.'),
  scrip_code: z.string().optional().describe('BSE/NSE scrip code. Do not pass with symbol.'),
  quarter: z.string().describe('Fiscal quarter key such as q4_26.'),
  detailed: z.boolean().default(true),
});

export const getEarningsDetail = new DynamicStructuredTool({
  name: 'drishti_get_earnings_detail',
  description: 'Fetch one Drishti earnings filing by symbol or scrip code and fiscal quarter.',
  schema: QuarterDetailSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>(
      '/v1/earnings/detail',
      { ...input, symbol: input.symbol?.toUpperCase() },
      { cacheable: true, ttlMs: TTL_6H },
    );
    return formatToolResult(data, [url]);
  },
});

export const getEarningsAttachments = new DynamicStructuredTool({
  name: 'drishti_get_earnings_attachments',
  description: 'Resolve attachment URLs for specific Drishti earnings filing ids. Use only when a source document is needed.',
  schema: AttachmentInputSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Schema<'BatchAttachmentLookupResponse'>>(
      '/v1/earnings/attachments',
      { ids: input.ids },
    );
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

export const getUpcomingEarnings = new DynamicStructuredTool({
  name: 'drishti_get_upcoming_earnings',
  description: 'List scheduled financial-results events that have not yet been published.',
  schema: z.object({
    symbols: SymbolsSchema.describe('Optional Indian listed symbols.'),
    scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes.'),
    ...PagingSchema,
  }),
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'UpcomingEarningsListItem'>>>(
      '/v1/earnings/upcoming',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_1H },
    );
    return formatToolResult(data, [url]);
  },
});

const ConcallsInputSchema = z.object({
  symbols: SymbolsSchema.describe('Optional Indian listed symbols.'),
  scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes.'),
  detailed: z.boolean().default(true),
  ...DateRangeSchema,
  ...PagingSchema,
});

export const getConcalls = new DynamicStructuredTool({
  name: 'drishti_get_concalls',
  description: 'List Drishti conference-call records with transcript/audio URLs, management commentary analysis, sentiment, quarter, and date.',
  schema: ConcallsInputSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>(
      '/v1/concalls',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_1H },
    );
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

export const getConcallsIndex = new DynamicStructuredTool({
  name: 'drishti_get_concalls_index',
  description: 'List lightweight Drishti concall index rows with symbol, quarter, and call date. Use for broad result-season call coverage scans.',
  schema: ConcallsInputSchema.omit({ detailed: true }),
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'LightweightIndexItem'>>>(
      '/v1/concalls/index',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_1H },
    );
    return formatToolResult(data, [url]);
  },
});

export const getConcallDetail = new DynamicStructuredTool({
  name: 'drishti_get_concall_detail',
  description: 'Fetch one Drishti conference-call record by symbol or scrip code and fiscal quarter.',
  schema: QuarterDetailSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>(
      '/v1/concalls/detail',
      { ...input, symbol: input.symbol?.toUpperCase() },
      { cacheable: true, ttlMs: TTL_6H },
    );
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

export const getConcallTranscript = new DynamicStructuredTool({
  name: 'drishti_get_concall_transcript',
  description: 'Resolve transcript and audio artifact URLs for one Drishti conference call by symbol or scrip code and quarter.',
  schema: QuarterDetailSchema.omit({ detailed: true }),
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Schema<'ConcallArtifactUrlsResponse'>>(
      '/v1/concalls/transcript',
      { ...input, symbol: input.symbol?.toUpperCase() },
    );
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

const SymbolQuarterInputSchema = z.object({
  symbol: z.string().describe('Indian listed symbol.'),
  quarter: z.string().describe('Fiscal quarter key such as q4_26.'),
});

function normalizeSymbolQuarterItems(items: Array<z.infer<typeof SymbolQuarterInputSchema>>): Schema<'SymbolQuarterLookupItem'>[] {
  return items.map((item) => ({
    symbol: uppercaseSymbol(item.symbol),
    quarter: item.quarter.trim().toLowerCase(),
  }));
}

const ConcallTranscriptBatchInputSchema = z.object({
  items: z.array(SymbolQuarterInputSchema).min(1).max(20).describe('Symbol and quarter pairs to resolve transcript/audio URLs for.'),
});

export const getConcallTranscriptsBatch = new DynamicStructuredTool({
  name: 'drishti_get_concall_transcripts_batch',
  description: 'Resolve transcript and audio artifact URLs for multiple Drishti conference calls by symbol and fiscal quarter.',
  schema: ConcallTranscriptBatchInputSchema,
  func: async (input) => {
    const body: Schema<'SymbolQuarterBatchRequest'> = {
      items: normalizeSymbolQuarterItems(input.items),
    };
    const { data, url } = await drishtiApi.post<Schema<'ConcallTranscriptBatchResponse'>>('/v1/concalls/transcripts', body);
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

export const getUpcomingConcalls = new DynamicStructuredTool({
  name: 'drishti_get_upcoming_concalls',
  description: 'List scheduled conference calls that have not yet occurred.',
  schema: z.object({
    symbols: SymbolsSchema.describe('Optional Indian listed symbols.'),
    scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes.'),
    detailed: z.boolean().default(false),
    ...PagingSchema,
  }),
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'UpcomingConcallListItem'> | Schema<'UpcomingConcallDetail'>>>(
      '/v1/concalls/upcoming',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_1H },
    );
    return formatToolResult(data, [url, ...extractUrls(data)]);
  },
});

const AlertsInputSchema = z.object({
  symbols: SymbolsSchema.describe('Optional Indian listed symbols. Omit for market-wide alerts.'),
  scrip_codes: SymbolsSchema.describe('Optional BSE/NSE scrip codes.'),
  type: SymbolsSchema.describe('Optional alert types, e.g. earnings, 52w_high, price_alert, volume_alert.'),
  important: z.boolean().default(false),
  ...DateRangeSchema,
  ...PagingSchema,
});

export const getAlerts = new DynamicStructuredTool({
  name: 'drishti_get_alerts',
  description: 'List Drishti alerts for market and corporate events, including 52-week, earnings, price, relative-volume, and volume alerts.',
  schema: AlertsInputSchema,
  func: async (input) => {
    const { data, url } = await drishtiGetWithFallback<Paginated<Schema<'Alert'>>>(
      '/v1/alerts',
      { ...input, symbols: uppercaseSymbols(input.symbols) },
      { cacheable: true, ttlMs: TTL_15M },
    );
    return formatToolResult(data, [url]);
  },
});

const SummaryModeSchema = z.enum(['exposure', 'intraday_movements', 'news_context']);
const SummaryItemInputSchema = z.object({
  symbol: z.string().describe('Indian listed symbol, e.g. RELIANCE or TCS.'),
  exposure: z.number().min(0).max(100).optional().describe('Optional portfolio exposure or weight from 0 to 100.'),
  label: z.string().optional().describe('Optional client-side label for this holding or watchlist item.'),
});

function uppercaseSummaryItems(items?: Array<z.infer<typeof SummaryItemInputSchema>>): Schema<'SummaryItem'>[] | undefined {
  return items?.map((item) => ({
    symbol: uppercaseSymbol(item.symbol),
    exposure: item.exposure,
    label: item.label,
  }));
}

function symbolsFromSummaryInput(input: {
  symbols?: string[];
  items?: Array<z.infer<typeof SummaryItemInputSchema>>;
}): string[] | undefined {
  const symbols = input.items && input.items.length > 0
    ? input.items.map((item) => item.symbol)
    : input.symbols;
  return uppercaseSymbols(symbols);
}

function dailySummaryBody(input: {
  symbols?: string[];
  items?: Array<z.infer<typeof SummaryItemInputSchema>>;
  mode: z.infer<typeof SummaryModeSchema>;
}): Schema<'SummaryRequest'> {
  const items = uppercaseSummaryItems(input.items);
  if (items && items.length > 0) {
    return { items, mode: input.mode };
  }
  return { symbols: uppercaseSymbols(input.symbols), mode: input.mode };
}

export const generateDailySummary = new DynamicStructuredTool({
  name: 'drishti_generate_daily_summary',
  description: 'Generate a Drishti portfolio/watchlist market summary from Indian listed symbols or weighted portfolio items.',
  schema: z.object({
    symbols: z.array(z.string()).max(50).optional().describe('Indian listed symbols to summarize when no weighted items are provided.'),
    items: z.array(SummaryItemInputSchema).max(50).optional().describe('Optional weighted portfolio/watchlist items. Prefer this when exposures are known.'),
    mode: SummaryModeSchema.default('news_context'),
  }).refine((input) => Boolean(input.symbols?.length || input.items?.length), {
    message: 'Provide symbols or items.',
  }),
  func: async (input) => {
    const { data, url } = await drishtiApi.post<Schema<'SummaryResponse'>>('/v1/daily-summary/', dailySummaryBody(input));
    return formatToolResult(data, [url]);
  },
});

const PortfolioBriefInputSchema = z.object({
  symbols: z.array(z.string()).max(50).optional().describe('Indian listed watchlist symbols. Use when portfolio weights are unknown.'),
  items: z.array(SummaryItemInputSchema).max(50).optional().describe('Weighted portfolio/watchlist items. Use exposure when known.'),
  mode: SummaryModeSchema.default('news_context'),
  lookback_days: z.number().int().min(1).max(30).default(3),
  include_daily_summary: z.boolean().default(true).describe('Call Drishti daily-summary to generate a markdown digest. Costs summary credits when a summary is returned.'),
  include_metadata: z.boolean().default(true),
  limit_per_section: z.number().int().min(1).max(50).default(20),
}).refine((input) => Boolean(input.symbols?.length || input.items?.length), {
  message: 'Provide symbols or items.',
});

export const portfolioBrief = new DynamicStructuredTool({
  name: 'portfolio_brief',
  description: 'Build a Drishti portfolio/watchlist brief for Indian equities using weighted daily summary, alerts, news, important announcements, upcoming events, and optional symbol metadata.',
  schema: PortfolioBriefInputSchema,
  func: async (input) => {
    const symbols = symbolsFromSummaryInput(input);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [summary, metadata, news, announcements, alerts, upcomingEarnings, upcomingConcalls] = await Promise.all([
      input.include_daily_summary
        ? evidencePost<Schema<'SummaryResponse'>>('/v1/daily-summary/', dailySummaryBody(input))
        : Promise.resolve<EvidenceResult<Schema<'SummaryResponse'>>>({ data: null }),
      input.include_metadata
        ? evidenceGet<Schema<'SymbolMetadataResponse'>>('/v1/symbols/metadata', { symbols }, { cacheable: true, ttlMs: TTL_6H })
        : Promise.resolve<EvidenceResult<Schema<'SymbolMetadataResponse'>>>({ data: null }),
      evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        from,
        important: true,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', { symbols, from, important: true, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'UpcomingEarningsListItem'>>>('/v1/earnings/upcoming', { symbols, limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'UpcomingConcallListItem'> | Schema<'UpcomingConcallDetail'>>>('/v1/concalls/upcoming', {
        symbols,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
    ]);

    return formatToolResult({
      symbols,
      items: uppercaseSummaryItems(input.items) ?? null,
      mode: input.mode,
      lookback_days: input.lookback_days,
      daily_summary: summary,
      metadata,
      news,
      important_announcements: announcements,
      alerts,
      upcoming_earnings: upcomingEarnings,
      upcoming_concalls: upcomingConcalls,
      synthesis_guidance: [
        'Lead with portfolio/watchlist implications, prioritizing higher exposure items when exposure is provided.',
        'Separate Drishti-generated summary text from structured evidence sections.',
        'Use dates, source links, alert reasons, announcement categories, and event dates from Drishti fields.',
      ],
    }, evidenceUrls([summary, metadata, news, announcements, alerts, upcomingEarnings, upcomingConcalls], [summary.data, news.data, upcomingConcalls.data]));
  },
});

type MetadataItem = Schema<'SymbolMetadataItem'>;

function groupMetadataByField(items: MetadataItem[], field: keyof Pick<MetadataItem, 'sector' | 'industry' | 'basic_industry' | 'theme'>): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const item of items) {
    const value = item[field] || 'Unclassified';
    groups[value] = [...(groups[value] ?? []), item.symbol];
  }
  return groups;
}

const PeerGroupResearchInputSchema = z.object({
  symbols: z.array(z.string()).min(2).max(20).describe('Indian listed peer symbols to compare. Provide the peer set explicitly.'),
  quarter: z.string().optional().describe('Optional fiscal quarter key such as q4_26 for earnings/concall context.'),
  lookback_days: z.number().int().min(1).max(365).default(90),
  include_earnings: z.boolean().default(true),
  include_concalls: z.boolean().default(true),
  include_news: z.boolean().default(true),
  limit_per_section: z.number().int().min(1).max(50).default(20),
});

export const peerGroupResearch = new DynamicStructuredTool({
  name: 'peer_group_research',
  description: 'Build a Drishti peer-group research pack for Indian equities using symbol metadata, sector/industry/theme grouping, earnings, concalls, news, announcements, alerts, and upcoming events.',
  schema: PeerGroupResearchInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const quarter = input.quarter?.trim().toLowerCase();
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [metadata, news, announcements, alerts, earnings, concalls, upcomingEarnings, upcomingConcalls] = await Promise.all([
      evidenceGet<Schema<'SymbolMetadataResponse'>>('/v1/symbols/metadata', { symbols }, { cacheable: true, ttlMs: TTL_6H }),
      input.include_news
        ? evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'NewsItem'>>>>({ data: null }),
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        from,
        important: true,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', { symbols, from, important: true, limit }, { cacheable: true, ttlMs: TTL_15M }),
      input.include_earnings
        ? evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
            symbols,
            quarter,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>>({ data: null }),
      input.include_concalls
        ? evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
            symbols,
            quarter,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>>({ data: null }),
      evidenceGet<Paginated<Schema<'UpcomingEarningsListItem'>>>('/v1/earnings/upcoming', { symbols, limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'UpcomingConcallListItem'> | Schema<'UpcomingConcallDetail'>>>('/v1/concalls/upcoming', {
        symbols,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
    ]);

    const metadataItems = metadata.data?.data ?? [];

    return formatToolResult({
      symbols,
      quarter: quarter ?? 'latest_or_recent',
      lookback_days: input.lookback_days,
      peer_classification: {
        by_sector: groupMetadataByField(metadataItems, 'sector'),
        by_industry: groupMetadataByField(metadataItems, 'industry'),
        by_basic_industry: groupMetadataByField(metadataItems, 'basic_industry'),
        by_theme: groupMetadataByField(metadataItems, 'theme'),
        market_cap_order: [...metadataItems]
          .sort((left, right) => (right.market_cap ?? 0) - (left.market_cap ?? 0))
          .map((item) => ({ symbol: item.symbol, market_cap: item.market_cap ?? null })),
      },
      metadata,
      news,
      important_announcements: announcements,
      alerts,
      earnings,
      concalls,
      upcoming_earnings: upcomingEarnings,
      upcoming_concalls: upcomingConcalls,
      synthesis_guidance: [
        'Start with how comparable the supplied symbols are based on sector, industry, basic_industry, theme, and market_cap fields.',
        'Compare recent operating/result context only from returned earnings, concall, news, alert, and announcement evidence.',
        'Do not claim this is an exhaustive sector screen; Drishti REST metadata here compares only the user-supplied peer set.',
      ],
    }, evidenceUrls([metadata, news, announcements, alerts, earnings, concalls, upcomingEarnings, upcomingConcalls], [news.data, concalls.data, upcomingConcalls.data]));
  },
});

const CompanyResearchInputSchema = z.object({
  symbol: z.string().describe('Indian listed symbol, e.g. RELIANCE, TCS, HDFCBANK.'),
  lookback_days: z.number().int().min(1).max(365).default(45),
  limit_per_section: z.number().int().min(1).max(20).default(8),
  include_attachment_urls: z.boolean().default(false).describe('Resolve filing/earnings attachment URLs for top records. Costs extra Drishti credits.'),
});

export const companyResearch = new DynamicStructuredTool({
  name: 'company_research',
  description: 'Build a Drishti company dossier for one Indian listed equity: metadata, news, important filings, earnings, concalls, alerts, and upcoming result/call events.',
  schema: CompanyResearchInputSchema,
  func: async (input) => {
    const symbol = uppercaseSymbol(input.symbol);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [
      metadata,
      news,
      announcements,
      earnings,
      concalls,
      alerts,
      upcomingEarnings,
      upcomingConcalls,
    ] = await Promise.all([
      evidenceGet<Schema<'SymbolMetadataResponse'>>('/v1/symbols/metadata', { symbols: [symbol] }, { cacheable: true, ttlMs: TTL_6H }),
      evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols: [symbol], from, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols: [symbol],
        from,
        important: true,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
        symbols: [symbol],
        from,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
        symbols: [symbol],
        from,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', { symbols: [symbol], from, important: true, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'UpcomingEarningsListItem'>>>('/v1/earnings/upcoming', { symbols: [symbol], limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'UpcomingConcallListItem'> | Schema<'UpcomingConcallDetail'>>>('/v1/concalls/upcoming', { symbols: [symbol], limit }, { cacheable: true, ttlMs: TTL_1H }),
    ]);

    const attachmentIds = input.include_attachment_urls
      ? [
          ...((announcements.data?.data ?? []).map((item) => item.id).filter(Boolean).slice(0, 5)),
          ...((earnings.data?.data ?? []).map((item) => item.id).filter(Boolean).slice(0, 5)),
        ]
      : [];

    const attachments = attachmentIds.length > 0
      ? await Promise.all([
          evidenceGet<Schema<'BatchAttachmentLookupResponse'>>('/v1/announcements/attachments', { ids: attachmentIds.slice(0, 5) }),
          evidenceGet<Schema<'BatchAttachmentLookupResponse'>>('/v1/earnings/attachments', { ids: attachmentIds.slice(5, 10) }),
        ])
      : [];

    const sections = {
      metadata,
      news,
      important_announcements: announcements,
      earnings,
      concalls,
      alerts,
      upcoming_earnings: upcomingEarnings,
      upcoming_concalls: upcomingConcalls,
      attachments,
    };

    return formatToolResult({
      symbol,
      lookback_days: input.lookback_days,
      sections,
      usage_notes: [
        'Dates, quarters, symbols, summaries, and URLs are Drishti evidence fields; preserve them in the final answer.',
        'This dossier does not include live quote snapshots or historical OHLCV because those endpoints are not present in the REST OpenAPI spec used by Max.',
      ],
    }, evidenceUrls(
      [metadata, news, announcements, earnings, concalls, alerts, upcomingEarnings, upcomingConcalls, ...attachments],
      [news.data, concalls.data, ...attachments.map((item) => item.data)],
    ));
  },
});

const EarningsAnalysisInputSchema = z.object({
  symbol: z.string().describe('Indian listed symbol, e.g. TCS.'),
  quarter: z.string().optional().describe('Optional Drishti fiscal quarter key such as q4_26. If omitted, fetches the latest detailed earnings list.'),
  lookback_days: z.number().int().min(1).max(365).default(120),
  include_attachment_urls: z.boolean().default(false).describe('Resolve earnings/announcement attachment URLs where possible. Costs extra Drishti credits.'),
});

export const earningsAnalysis = new DynamicStructuredTool({
  name: 'earnings_analysis',
  description: 'Build a Drishti results-analysis pack for an Indian company: earnings filing/detail, concall commentary, transcript URL, related result announcements, and news.',
  schema: EarningsAnalysisInputSchema,
  func: async (input) => {
    const symbol = uppercaseSymbol(input.symbol);
    const from = daysAgoIso(input.lookback_days);
    const quarter = input.quarter?.trim().toLowerCase();

    const earningsRequest = quarter
      ? evidenceGet<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>('/v1/earnings/detail', {
          symbol,
          quarter,
          detailed: true,
        }, { cacheable: true, ttlMs: TTL_6H })
      : evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
          symbols: [symbol],
          from,
          detailed: true,
          limit: 5,
        }, { cacheable: true, ttlMs: TTL_1H });

    const concallRequest = quarter
      ? evidenceGet<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>('/v1/concalls/detail', {
          symbol,
          quarter,
          detailed: true,
        }, { cacheable: true, ttlMs: TTL_6H })
      : evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
          symbols: [symbol],
          from,
          detailed: true,
          limit: 5,
        }, { cacheable: true, ttlMs: TTL_1H });

    const transcriptRequest = quarter
      ? evidenceGet<Schema<'ConcallArtifactUrlsResponse'>>('/v1/concalls/transcript', { symbol, quarter })
      : Promise.resolve<EvidenceResult<Schema<'ConcallArtifactUrlsResponse'>>>({ data: null });

    const [earnings, concall, transcript, announcements, news, alerts] = await Promise.all([
      earningsRequest,
      concallRequest,
      transcriptRequest,
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols: [symbol],
        from,
        categories: ['Media Release of Financial Results', 'Unaudited Financial Results', 'Investor Presentation', 'Concall'],
        detailed: true,
        limit: 10,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols: [symbol], from, limit: 10 }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', { symbols: [symbol], from, type: ['earnings', 'high_growth_concalls'], limit: 10 }, { cacheable: true, ttlMs: TTL_15M }),
    ]);

    const earningIds = input.include_attachment_urls
      ? (Array.isArray((earnings.data as Paginated<Schema<'EarningsListItem'>> | null)?.data)
          ? ((earnings.data as Paginated<Schema<'EarningsListItem'>>).data.map((item) => item.id).slice(0, 5))
          : typeof (earnings.data as Schema<'EarningsListItem'> | null)?.id === 'string'
            ? [(earnings.data as Schema<'EarningsListItem'>).id]
            : [])
      : [];
    const announcementIds = input.include_attachment_urls
      ? (announcements.data?.data ?? []).map((item) => item.id).filter(Boolean).slice(0, 5)
      : [];

    const [earningsAttachments, announcementAttachments] = await Promise.all([
      earningIds.length > 0
        ? evidenceGet<Schema<'BatchAttachmentLookupResponse'>>('/v1/earnings/attachments', { ids: earningIds })
        : Promise.resolve<EvidenceResult<Schema<'BatchAttachmentLookupResponse'>>>({ data: null }),
      announcementIds.length > 0
        ? evidenceGet<Schema<'BatchAttachmentLookupResponse'>>('/v1/announcements/attachments', { ids: announcementIds })
        : Promise.resolve<EvidenceResult<Schema<'BatchAttachmentLookupResponse'>>>({ data: null }),
    ]);

    return formatToolResult({
      symbol,
      quarter: quarter ?? 'latest_available',
      lookback_days: input.lookback_days,
      earnings,
      concall,
      transcript,
      related_announcements: announcements,
      related_news: news,
      alerts,
      attachments: {
        earnings: earningsAttachments,
        announcements: announcementAttachments,
      },
      analysis_prompt: [
        'Compare reported earnings table metrics, summary language, concall guidance, red flags, alerts, and news.',
        'Call out missing quarter/detail records explicitly rather than inferring them.',
      ],
    }, evidenceUrls(
      [earnings, concall, transcript, announcements, news, alerts, earningsAttachments, announcementAttachments],
      [concall.data, transcript.data, news.data, earningsAttachments.data, announcementAttachments.data],
    ));
  },
});

type EarningsMetricValue = {
  current_period_value?: unknown;
  yoy_percent?: unknown;
  qoq_percent?: unknown;
  unit?: unknown;
  [key: string]: unknown;
};

type EarningsMetricRow = {
  id: string;
  symbol: string;
  company_name?: string | null;
  quarter?: string | null;
  date?: string | null;
  earnings_significant?: boolean;
  summary?: string | null;
  metrics: Record<string, EarningsMetricValue>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function earningsDetailsFromResponse(data: Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>> | null): Schema<'EarningsDetail'>[] {
  return (data?.data ?? []).filter((item): item is Schema<'EarningsDetail'> => 'earnings_table' in item);
}

function normalizeMetricTable(table: { [key: string]: unknown } | null | undefined, requestedMetrics?: string[]): Record<string, EarningsMetricValue> {
  if (!table) return {};
  const requested = requestedMetrics?.map((metric) => metric.trim().toLowerCase()).filter(Boolean);
  const entries = Object.entries(table).filter(([metric]) => !requested || requested.includes(metric.toLowerCase()));

  return Object.fromEntries(entries.map(([metric, value]) => [
    metric,
    isRecord(value) ? value as EarningsMetricValue : { current_period_value: value },
  ]));
}

function earningsMetricRows(items: Schema<'EarningsDetail'>[], requestedMetrics?: string[]): EarningsMetricRow[] {
  return items.map((item) => ({
    id: item.id,
    symbol: item.symbol,
    company_name: item.company_name,
    quarter: item.quarter,
    date: item.date,
    earnings_significant: item.earnings_significant,
    summary: item.summary,
    metrics: normalizeMetricTable(item.earnings_table, requestedMetrics),
  }));
}

const EarningsMetricComparisonInputSchema = z.object({
  symbols: z.array(z.string()).min(1).max(20).describe('Indian listed symbols to compare.'),
  quarter: z.string().optional().describe('Optional fiscal quarter key such as q4_26.'),
  metrics: z.array(z.string()).max(20).optional().describe('Optional metric keys from Drishti earnings_table, e.g. revenue, ebitda, pat. Omit to return all returned metrics.'),
  lookback_days: z.number().int().min(1).max(365).default(120),
  include_concall_context: z.boolean().default(true),
  include_result_announcements: z.boolean().default(true),
  limit_per_section: z.number().int().min(1).max(50).default(20),
});

export const earningsMetricComparison = new DynamicStructuredTool({
  name: 'earnings_metric_comparison',
  description: 'Compare Drishti structured earnings_table metrics across Indian companies and/or quarters, preserving Drishti metric keys, units, current-period values, and growth fields.',
  schema: EarningsMetricComparisonInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const quarter = input.quarter?.trim().toLowerCase();
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [metadata, earnings, concalls, announcements] = await Promise.all([
      evidenceGet<Schema<'SymbolMetadataResponse'>>('/v1/symbols/metadata', { symbols }, { cacheable: true, ttlMs: TTL_6H }),
      evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
        symbols,
        quarter,
        from,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
      input.include_concall_context
        ? evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
            symbols,
            quarter,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>>({ data: null }),
      input.include_result_announcements
        ? evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
            symbols,
            from,
            categories: ['Media Release of Financial Results', 'Unaudited Financial Results', 'Financial Results', 'Result', 'Investor Presentation'],
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Schema<'AnnouncementPaginatedResponse'>>>({ data: null }),
    ]);

    const rows = earningsMetricRows(earningsDetailsFromResponse(earnings.data), input.metrics);
    const metricCatalog = [...new Set(rows.flatMap((row) => Object.keys(row.metrics)))].sort();

    return formatToolResult({
      symbols,
      quarter: quarter ?? 'latest_or_recent',
      requested_metrics: input.metrics ?? 'all_returned_metrics',
      lookback_days: input.lookback_days,
      metric_catalog: metricCatalog,
      metric_rows: rows,
      metadata,
      earnings,
      concall_context: concalls,
      result_announcements: announcements,
      synthesis_guidance: [
        'Compare only metrics present in Drishti earnings_table; call out missing metrics by company/quarter.',
        'Preserve Drishti units and growth fields such as yoy_percent/qoq_percent instead of normalizing into a legacy statement schema.',
        'Use summaries, concalls, and result announcements as context, not as substitutes for missing metric values.',
      ],
    }, evidenceUrls([metadata, earnings, concalls, announcements], [concalls.data]));
  },
});

const MarketPulseInputSchema = z.object({
  symbols: z.array(z.string()).max(50).optional().describe('Optional watchlist symbols. Omit for broad market pulse where supported.'),
  lookback_days: z.number().int().min(1).max(30).default(3),
  limit_per_section: z.number().int().min(1).max(50).default(20),
  include_daily_summary: z.boolean().default(false).describe('Generate a Drishti daily summary for the watchlist. Requires symbols and costs summary credits when a summary is returned.'),
});

export const marketPulse = new DynamicStructuredTool({
  name: 'market_pulse',
  description: 'Build a Drishti Indian-market pulse from recent news, important announcements, alerts, upcoming earnings, upcoming concalls, and optional watchlist daily summary.',
  schema: MarketPulseInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [news, announcements, alerts, upcomingEarnings, upcomingConcalls, summary] = await Promise.all([
      evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        from,
        important: true,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', { symbols, from, important: true, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'UpcomingEarningsListItem'>>>('/v1/earnings/upcoming', { symbols, limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'UpcomingConcallListItem'> | Schema<'UpcomingConcallDetail'>>>('/v1/concalls/upcoming', { symbols, limit }, { cacheable: true, ttlMs: TTL_1H }),
      input.include_daily_summary && symbols && symbols.length > 0
        ? evidencePost<Schema<'SummaryResponse'>>('/v1/daily-summary/', { symbols, mode: 'news_context' })
        : Promise.resolve<EvidenceResult<Schema<'SummaryResponse'>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols: symbols ?? 'market_wide',
      lookback_days: input.lookback_days,
      news,
      important_announcements: announcements,
      alerts,
      upcoming_earnings: upcomingEarnings,
      upcoming_concalls: upcomingConcalls,
      daily_summary: summary,
      synthesis_guidance: [
        'Group the pulse by companies/themes with dates and sources.',
        'Do not describe this as a complete market breadth or price-mover report unless price/top-mover endpoints are available.',
      ],
    }, evidenceUrls([news, announcements, alerts, upcomingEarnings, upcomingConcalls, summary], [news.data, summary.data]));
  },
});

const PRICE_VOLUME_ALERT_TYPE_MAP = {
  price: ['price_alert', '52w_high', '52w_low'],
  volume: ['volume_alert', 'rvol_alert'],
  breakout: ['52w_high', '52w_low', 'price_alert', 'volume_alert', 'rvol_alert'],
  earnings: ['earnings', 'high_growth_concalls', 'price_alert', 'volume_alert'],
} as const;

const PriceVolumeSignalTypeSchema = z.enum(['all', 'price', 'volume', 'breakout', 'earnings']);

function alertTypesForSignalType(signalType: z.infer<typeof PriceVolumeSignalTypeSchema>): string[] {
  if (signalType === 'all') {
    return ['52w_high', '52w_low', 'price_alert', 'volume_alert', 'rvol_alert', 'earnings', 'high_growth_concalls'];
  }
  return [...PRICE_VOLUME_ALERT_TYPE_MAP[signalType]];
}

const PriceVolumeSignalExplainerInputSchema = z.object({
  symbols: z.array(z.string()).min(1).max(20).describe('Indian listed symbols to explain price/volume/alert signals for.'),
  signal_type: PriceVolumeSignalTypeSchema.default('all'),
  lookback_days: z.number().int().min(1).max(30).default(7),
  include_news_context: z.boolean().default(true),
  include_filing_context: z.boolean().default(true),
  include_result_context: z.boolean().default(false),
  limit_per_section: z.number().int().min(1).max(30).default(10),
});

export const priceVolumeSignalExplainer = new DynamicStructuredTool({
  name: 'price_volume_signal_explainer',
  description: 'Explain Indian equity price/volume/52-week alert signals using Drishti alerts, primary drivers, news, announcements, and optional earnings/concall context. Does not compute raw OHLCV returns.',
  schema: PriceVolumeSignalExplainerInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;
    const alertTypes = alertTypesForSignalType(input.signal_type);

    const [alerts, news, announcements, earnings, concalls] = await Promise.all([
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', {
        symbols,
        type: alertTypes,
        from,
        important: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      input.include_news_context
        ? evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'NewsItem'>>>>({ data: null }),
      input.include_filing_context
        ? evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
            symbols,
            from,
            important: true,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Schema<'AnnouncementPaginatedResponse'>>>({ data: null }),
      input.include_result_context
        ? evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
            symbols,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>>({ data: null }),
      input.include_result_context
        ? evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
            symbols,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols,
      signal_type: input.signal_type,
      mapped_alert_types: alertTypes,
      lookback_days: input.lookback_days,
      alerts,
      news_context: news,
      filing_context: announcements,
      earnings_context: earnings,
      concall_context: concalls,
      synthesis_guidance: [
        'Lead with Drishti alert type, timestamp, reason, and primary_drivers when present.',
        'Use news and filings as context for why a signal may have appeared; separate confirmed disclosures from commentary.',
        'Do not report exact price returns, volumes, VWAP, candles, or technical indicators unless a Drishti field explicitly provides them.',
      ],
    }, evidenceUrls([alerts, news, announcements, earnings, concalls], [news.data, concalls.data]));
  },
});

const CORPORATE_ACTION_CATEGORY_MAP = {
  dividends: ['Dividend'],
  capital_actions: ['Buyback', 'Rights Issue', 'Bonus Issue', 'Stock Split', 'Issue of Securities', 'Conversion of Warrants', 'Redemption of Securities', 'Offer for Sale (OFS)'],
  m_and_a: ['Acquisition', 'Merger', 'Demerger', 'Divestment', 'Joint Venture', 'De-listing', 'Liquidation/Insolvency'],
  management: ['Change in Management', 'Outcome of Board Meeting'],
  orders_and_expansion: ['Award/Receipt of Order', 'Capex/Expansion', 'Partnership', 'Regulatory Approval'],
  regulatory_and_legal: ['Litigation', 'Penalty', 'Credit Rating', 'Regulatory Authority Inspection', 'Income Tax', 'GST Payment'],
  reports_and_presentations: ['Investor Presentation', 'Annual Report', 'Media Release of Financial Results', 'Unaudited Financial Results'],
} as const;

const CorporateActionTypeSchema = z.enum([
  'all',
  'dividends',
  'capital_actions',
  'm_and_a',
  'management',
  'orders_and_expansion',
  'regulatory_and_legal',
  'reports_and_presentations',
]);

function categoriesForCorporateAction(actionType: z.infer<typeof CorporateActionTypeSchema>): string[] | undefined {
  if (actionType === 'all') return undefined;
  return [...CORPORATE_ACTION_CATEGORY_MAP[actionType]];
}

const ORDERS_CAPEX_CATEGORY_MAP = {
  orders: ['Award/Receipt of Order', 'Award/Win of auction/tender'],
  capex: ['Capex/Expansion', 'Regulatory Approval'],
  partnerships: ['Partnership', 'Joint Venture'],
  business_updates: ['Business Development', 'Company Update', 'Incorporation of Subsidiary'],
} as const;

const OrdersCapexFocusSchema = z.enum(['all', 'orders', 'capex', 'partnerships', 'business_updates']);

function categoriesForOrdersCapexFocus(focus: z.infer<typeof OrdersCapexFocusSchema>): string[] {
  if (focus === 'all') {
    return Object.values(ORDERS_CAPEX_CATEGORY_MAP).flatMap((categories) => [...categories]);
  }
  return [...ORDERS_CAPEX_CATEGORY_MAP[focus]];
}

const OrdersCapexTrackerInputSchema = z.object({
  symbols: z.array(z.string()).max(20).optional().describe('Optional Indian listed symbols. Omit for market-wide order/capex monitoring.'),
  focus: OrdersCapexFocusSchema.default('all'),
  lookback_days: z.number().int().min(1).max(365).default(90),
  include_news_context: z.boolean().default(true),
  include_execution_context: z.boolean().default(true).describe('Include recent earnings and concalls for order-book, capex, capacity, and execution commentary.'),
  limit_per_section: z.number().int().min(1).max(50).default(20),
});

export const ordersCapexTracker = new DynamicStructuredTool({
  name: 'orders_capex_tracker',
  description: 'Track Indian order wins, tenders, capex/expansion, partnerships, regulatory approvals, and business-development updates using Drishti announcements with news, alerts, earnings, and concall context.',
  schema: OrdersCapexTrackerInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;
    const categories = categoriesForOrdersCapexFocus(input.focus);

    const [announcements, alerts, news, earnings, concalls] = await Promise.all([
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        categories,
        from,
        important: false,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', {
        symbols,
        from,
        important: true,
        type: ['price_alert', 'volume_alert', 'rvol_alert', '52w_high'],
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      input.include_news_context
        ? evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'NewsItem'>>>>({ data: null }),
      input.include_execution_context
        ? evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
            symbols,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>>({ data: null }),
      input.include_execution_context
        ? evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
            symbols,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols: symbols ?? 'market_wide',
      focus: input.focus,
      mapped_categories: categories,
      lookback_days: input.lookback_days,
      order_capex_announcements: announcements,
      alerts,
      news_context: news,
      earnings_execution_context: earnings,
      concall_execution_context: concalls,
      synthesis_guidance: [
        'Group updates by order wins/tenders, capex/expansion, partnerships, approvals, and business updates.',
        'Preserve announcement category, dates, company, title/headline, summary, extracted facts, and source URLs.',
        'Do not infer order value, execution timeline, capacity, revenue conversion, or margins unless Drishti evidence includes them.',
      ],
    }, evidenceUrls([announcements, alerts, news, earnings, concalls], [news.data, concalls.data]));
  },
});

function uniqueNonEmpty(values: Array<string | null | undefined>, max = 20): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))].slice(0, max);
}

const FilingSourcePackInputSchema = z.object({
  symbols: z.array(z.string()).max(20).optional().describe('Optional Indian listed symbols to discover recent source documents for.'),
  announcement_ids: z.array(z.string()).max(20).optional().describe('Known Drishti announcement ids to resolve attachment URLs for.'),
  earnings_ids: z.array(z.string()).max(20).optional().describe('Known Drishti earnings filing ids to resolve attachment URLs for.'),
  categories: z.array(z.string()).max(20).optional().describe('Optional announcement categories when discovering source documents.'),
  quarter: z.string().optional().describe('Optional fiscal quarter key such as q4_26 for earnings discovery.'),
  lookback_days: z.number().int().min(1).max(365).default(90),
  include_announcements: z.boolean().default(true),
  include_earnings: z.boolean().default(true),
  limit_per_section: z.number().int().min(1).max(20).default(10),
}).refine((input) => Boolean(
  input.announcement_ids?.length ||
  input.earnings_ids?.length ||
  input.symbols?.length
), {
  message: 'Provide symbols or explicit announcement_ids/earnings_ids.',
});

export const filingSourcePack = new DynamicStructuredTool({
  name: 'filing_source_pack',
  description: 'Build a Drishti source-document pack by resolving attachment URLs for Indian exchange announcements and earnings filings, optionally discovering recent ids by symbols/categories/quarter first.',
  schema: FilingSourcePackInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const quarter = input.quarter?.trim().toLowerCase();
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [announcements, earnings] = await Promise.all([
      input.include_announcements && symbols && symbols.length > 0
        ? evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
            symbols,
            categories: input.categories,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Schema<'AnnouncementPaginatedResponse'>>>({ data: null }),
      input.include_earnings && symbols && symbols.length > 0
        ? evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
            symbols,
            quarter,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>>({ data: null }),
    ]);

    const discoveredAnnouncementIds = announcements.data?.data.map((item) => item.id) ?? [];
    const discoveredEarningsIds = earnings.data?.data.map((item) => item.id) ?? [];
    const announcementIds = uniqueNonEmpty([...(input.announcement_ids ?? []), ...discoveredAnnouncementIds]);
    const earningsIds = uniqueNonEmpty([...(input.earnings_ids ?? []), ...discoveredEarningsIds]);

    const [announcementAttachments, earningsAttachments] = await Promise.all([
      announcementIds.length > 0
        ? evidenceGet<Schema<'BatchAttachmentLookupResponse'>>('/v1/announcements/attachments', { ids: announcementIds })
        : Promise.resolve<EvidenceResult<Schema<'BatchAttachmentLookupResponse'>>>({ data: null }),
      earningsIds.length > 0
        ? evidenceGet<Schema<'BatchAttachmentLookupResponse'>>('/v1/earnings/attachments', { ids: earningsIds })
        : Promise.resolve<EvidenceResult<Schema<'BatchAttachmentLookupResponse'>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols: symbols ?? null,
      quarter: quarter ?? null,
      lookback_days: input.lookback_days,
      discovered_records: {
        announcements,
        earnings,
      },
      resolved_ids: {
        announcements: announcementIds,
        earnings: earningsIds,
      },
      attachments: {
        announcements: announcementAttachments,
        earnings: earningsAttachments,
      },
      synthesis_guidance: [
        'Use attachment status fields to distinguish ready, unavailable, invalid, and not-found documents.',
        'Preserve ids, source record titles/categories/dates, and resolved attachment URLs.',
        'Do not claim to have read PDFs unless the agent fetches or reads the returned URLs separately.',
      ],
    }, evidenceUrls(
      [announcements, earnings, announcementAttachments, earningsAttachments],
      [announcementAttachments.data, earningsAttachments.data],
    ));
  },
});

export const corporateActionMonitor = new DynamicStructuredTool({
  name: 'corporate_action_monitor',
  description: 'Monitor Drishti exchange announcements for Indian corporate actions such as dividends, buybacks, M&A, management changes, orders, capex, legal/regulatory updates, and investor presentations.',
  schema: z.object({
    symbols: z.array(z.string()).max(20).optional().describe('Optional Indian listed symbols. Omit for market-wide corporate action monitoring.'),
    action_type: CorporateActionTypeSchema.default('all'),
    lookback_days: z.number().int().min(1).max(365).default(30),
    important_only: z.boolean().default(true),
    include_news_context: z.boolean().default(true),
    limit: z.number().int().min(1).max(50).default(20),
  }),
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const categories = categoriesForCorporateAction(input.action_type);

    const [announcements, alerts, news] = await Promise.all([
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        categories,
        from,
        important: input.important_only,
        detailed: true,
        limit: input.limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', { symbols, from, important: input.important_only, limit: input.limit }, { cacheable: true, ttlMs: TTL_15M }),
      input.include_news_context
        ? evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit: input.limit }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'NewsItem'>>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols: symbols ?? 'market_wide',
      action_type: input.action_type,
      mapped_categories: categories ?? 'all_categories',
      lookback_days: input.lookback_days,
      announcements,
      alerts,
      news_context: news,
      synthesis_guidance: [
        'Group by corporate action category and company.',
        'Preserve announcement dates, category, headline/title, summary, important flag, and source URLs.',
        'Do not infer transaction values, record dates, or ratios unless present in Drishti fields.',
      ],
    }, evidenceUrls([announcements, alerts, news], [news.data]));
  },
});

const RiskMonitorInputSchema = z.object({
  symbols: z.array(z.string()).min(1).max(20).describe('Indian listed symbols to monitor.'),
  lookback_days: z.number().int().min(1).max(365).default(90),
  include_concalls: z.boolean().default(true),
  limit_per_section: z.number().int().min(1).max(30).default(10),
});

export const riskMonitor = new DynamicStructuredTool({
  name: 'risk_monitor',
  description: 'Monitor risk signals for Indian equities using Drishti negative news, important announcements, legal/regulatory categories, alerts, and concall red-flag commentary.',
  schema: RiskMonitorInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;
    const riskCategories = [
      'Litigation',
      'Penalty',
      'Reduction of Stake',
      'Change in Management',
      'Liquidation/Insolvency',
      'Regulatory Authority Inspection',
      'Credit Rating',
      'Income Tax',
      'GST Payment',
      'Incident/Accident',
    ];

    const [negativeNews, riskAnnouncements, alerts, concalls] = await Promise.all([
      evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, sentiment: 'negative', from, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        categories: riskCategories,
        from,
        important: false,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', {
        symbols,
        from,
        important: true,
        type: ['52w_low', 'price_alert', 'rvol_alert', 'volume_alert', 'earnings', 'high_growth_concalls'],
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      input.include_concalls
        ? evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
            symbols,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols,
      lookback_days: input.lookback_days,
      risk_categories: riskCategories,
      negative_news: negativeNews,
      risk_announcements: riskAnnouncements,
      alerts,
      concall_commentary: concalls,
      synthesis_guidance: [
        'Separate confirmed disclosures from market/news commentary.',
        'Highlight severity, recency, affected company, and whether the source is announcement, news, alert, or concall.',
        'For concalls, use returned redflags/expanded_analysis fields when present; do not invent management commentary.',
      ],
    }, evidenceUrls([negativeNews, riskAnnouncements, alerts, concalls], [negativeNews.data, concalls.data]));
  },
});

export const eventCalendar = new DynamicStructuredTool({
  name: 'event_calendar',
  description: 'Build a Drishti event calendar for Indian equities using upcoming earnings, upcoming concalls, meeting-intimation announcements, and recent result/concall filings.',
  schema: z.object({
    symbols: z.array(z.string()).max(20).optional().describe('Optional Indian listed symbols. Omit for market-wide upcoming events.'),
    lookback_days: z.number().int().min(1).max(90).default(14),
    limit_per_section: z.number().int().min(1).max(50).default(20),
  }),
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [upcomingEarnings, upcomingConcalls, meetingAnnouncements, recentEarnings, recentConcalls] = await Promise.all([
      evidenceGet<Paginated<Schema<'UpcomingEarningsListItem'>>>('/v1/earnings/upcoming', { symbols, limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'UpcomingConcallListItem'> | Schema<'UpcomingConcallDetail'>>>('/v1/concalls/upcoming', {
        symbols,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        categories: ['Meeting Intimation', 'Concall', 'Outcome of Board Meeting'],
        from,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
        symbols,
        from,
        detailed: false,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
        symbols,
        from,
        detailed: false,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
    ]);

    return formatToolResult({
      symbols: symbols ?? 'market_wide',
      lookback_days: input.lookback_days,
      upcoming_earnings: upcomingEarnings,
      upcoming_concalls: upcomingConcalls,
      meeting_announcements: meetingAnnouncements,
      recent_earnings_filings: recentEarnings,
      recent_concalls: recentConcalls,
      synthesis_guidance: [
        'Present upcoming dated events first, then recent filed events.',
        'Preserve quarter keys, meeting dates, event dates, purpose/title, and symbols.',
        'Do not infer future dates when Drishti does not return them.',
      ],
    }, evidenceUrls([upcomingEarnings, upcomingConcalls, meetingAnnouncements, recentEarnings, recentConcalls], [upcomingConcalls.data]));
  },
});

const ResultSeasonTrackerInputSchema = z.object({
  symbols: z.array(z.string()).max(20).optional().describe('Optional Indian listed symbols or watchlist. Omit for broad result-season monitoring.'),
  quarter: z.string().optional().describe('Optional fiscal quarter key such as q4_26.'),
  lookback_days: z.number().int().min(1).max(180).default(45),
  include_significant_details: z.boolean().default(true).describe('Fetch detailed earnings rows so Drishti earnings_significant and earnings_table fields can be used.'),
  include_news_context: z.boolean().default(true),
  limit_per_section: z.number().int().min(1).max(50).default(20),
});

export const resultSeasonTracker = new DynamicStructuredTool({
  name: 'result_season_tracker',
  description: 'Track Indian result season using Drishti earnings index, concall index, upcoming earnings/calls, result announcements, and optional detailed significant earnings/news context.',
  schema: ResultSeasonTrackerInputSchema,
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const quarter = input.quarter?.trim().toLowerCase();
    const limit = input.limit_per_section;

    const [earningsIndex, concallIndex, upcomingEarnings, upcomingConcalls, significantEarnings, resultAnnouncements, news] = await Promise.all([
      evidenceGet<Paginated<Schema<'LightweightIndexItem'>>>('/v1/earnings/index', { symbols, from, quarter, limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'LightweightIndexItem'>>>('/v1/concalls/index', { symbols, from, quarter, limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'UpcomingEarningsListItem'>>>('/v1/earnings/upcoming', { symbols, limit }, { cacheable: true, ttlMs: TTL_1H }),
      evidenceGet<Paginated<Schema<'UpcomingConcallListItem'> | Schema<'UpcomingConcallDetail'>>>('/v1/concalls/upcoming', {
        symbols,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_1H }),
      input.include_significant_details
        ? evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
            symbols,
            from,
            quarter,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>>({ data: null }),
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        from,
        categories: ['Media Release of Financial Results', 'Unaudited Financial Results', 'Financial Results', 'Result', 'Concall'],
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      input.include_news_context
        ? evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'NewsItem'>>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols: symbols ?? 'market_wide',
      quarter: quarter ?? 'latest_or_recent',
      lookback_days: input.lookback_days,
      reported_earnings_index: earningsIndex,
      reported_concalls_index: concallIndex,
      upcoming_earnings: upcomingEarnings,
      upcoming_concalls: upcomingConcalls,
      significant_earnings_details: significantEarnings,
      result_announcements: resultAnnouncements,
      news_context: news,
      synthesis_guidance: [
        'Separate companies that have reported results from companies with only upcoming events.',
        'Use earnings_significant and earnings_table only when Drishti returned detailed earnings rows.',
        'Flag missing concall coverage by comparing reported earnings index with reported concalls index for the same symbol/quarter.',
      ],
    }, evidenceUrls([earningsIndex, concallIndex, upcomingEarnings, upcomingConcalls, significantEarnings, resultAnnouncements, news], [upcomingConcalls.data, news.data]));
  },
});

const GOVERNANCE_CATEGORY_MAP = {
  management_and_promoter: ['Change in Management', 'Change In Management', 'Insider Trading / SAST', 'Reduction of Stake', 'Outcome of Board Meeting'],
  compliance_and_legal: ['Litigation', 'Penalty', 'Regulatory Authority Inspection', 'Income Tax', 'GST Payment', 'Incident/Accident'],
  capital_and_ownership: ['Issue of Securities', 'Conversion of Warrants', 'Rights Issue', 'Buyback', 'Offer for Sale (OFS)', 'De-listing'],
  reports_and_controls: ['Annual Report', 'Monitoring Agency Report', 'Integrated Filing'],
} as const;

const GovernanceFocusSchema = z.enum([
  'all',
  'management_and_promoter',
  'compliance_and_legal',
  'capital_and_ownership',
  'reports_and_controls',
]);

function categoriesForGovernanceFocus(focus: z.infer<typeof GovernanceFocusSchema>): string[] | undefined {
  if (focus === 'all') {
    return Object.values(GOVERNANCE_CATEGORY_MAP).flatMap((categories) => [...categories]);
  }
  return [...GOVERNANCE_CATEGORY_MAP[focus]];
}

export const governanceMonitor = new DynamicStructuredTool({
  name: 'governance_monitor',
  description: 'Monitor Indian governance signals using Drishti management/promoter, compliance/legal, ownership/capital-action announcements, negative news, alerts, and optional concall commentary.',
  schema: z.object({
    symbols: z.array(z.string()).min(1).max(20).describe('Indian listed symbols to monitor.'),
    focus: GovernanceFocusSchema.default('all'),
    lookback_days: z.number().int().min(1).max(365).default(120),
    include_concalls: z.boolean().default(true),
    limit_per_section: z.number().int().min(1).max(30).default(10),
  }),
  func: async (input) => {
    const symbols = uppercaseSymbols(input.symbols);
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;
    const categories = categoriesForGovernanceFocus(input.focus);

    const [governanceAnnouncements, negativeNews, alerts, concalls] = await Promise.all([
      evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
        symbols,
        categories,
        from,
        important: false,
        detailed: true,
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, sentiment: 'negative', from, limit }, { cacheable: true, ttlMs: TTL_15M }),
      evidenceGet<Paginated<Schema<'Alert'>>>('/v1/alerts', {
        symbols,
        from,
        important: true,
        type: ['price_alert', 'volume_alert', 'rvol_alert', '52w_low'],
        limit,
      }, { cacheable: true, ttlMs: TTL_15M }),
      input.include_concalls
        ? evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
            symbols,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>>({ data: null }),
    ]);

    return formatToolResult({
      symbols,
      focus: input.focus,
      mapped_categories: categories,
      lookback_days: input.lookback_days,
      governance_announcements: governanceAnnouncements,
      negative_news: negativeNews,
      alerts,
      concall_commentary: concalls,
      synthesis_guidance: [
        'Treat exchange announcements as primary evidence and news/concall/alert items as context.',
        'Group issues by governance theme, company, date, and source type.',
        'Do not infer fraud, misconduct, promoter intent, or regulatory outcomes unless Drishti evidence states them.',
      ],
    }, evidenceUrls([governanceAnnouncements, negativeNews, alerts, concalls], [negativeNews.data, concalls.data]));
  },
});

const ConcallSourcePackInputSchema = z.object({
  items: z.array(SymbolQuarterInputSchema).min(1).max(20).optional().describe('Explicit symbol and quarter pairs. Preferred when the quarters are known.'),
  symbols: z.array(z.string()).max(20).optional().describe('Symbols to pair with `quarter` when explicit items are not supplied.'),
  quarter: z.string().optional().describe('Fiscal quarter key to use with symbols, e.g. q4_26.'),
  include_concall_details: z.boolean().default(true),
  include_result_context: z.boolean().default(true),
  include_news_context: z.boolean().default(false),
  lookback_days: z.number().int().min(1).max(365).default(120),
  limit_per_section: z.number().int().min(1).max(20).default(10),
}).refine((input) => Boolean(input.items?.length || (input.symbols?.length && input.quarter)), {
  message: 'Provide items, or provide symbols with quarter.',
});

function symbolQuarterItemsFromInput(input: z.infer<typeof ConcallSourcePackInputSchema>): Schema<'SymbolQuarterLookupItem'>[] {
  if (input.items && input.items.length > 0) {
    return normalizeSymbolQuarterItems(input.items);
  }
  const quarter = input.quarter?.trim().toLowerCase() ?? '';
  return (uppercaseSymbols(input.symbols) ?? []).map((symbol) => ({ symbol, quarter }));
}

export const concallSourcePack = new DynamicStructuredTool({
  name: 'concall_source_pack',
  description: 'Build a source pack for Indian conference-call research by resolving transcript/audio URLs in batch and pairing them with Drishti concall summaries, result filings, and optional news context.',
  schema: ConcallSourcePackInputSchema,
  func: async (input) => {
    const items = symbolQuarterItemsFromInput(input);
    const symbols = [...new Set(items.map((item) => item.symbol))];
    const quarters = [...new Set(items.map((item) => item.quarter))];
    const from = daysAgoIso(input.lookback_days);
    const limit = input.limit_per_section;

    const [artifacts, concalls, earnings, announcements, news] = await Promise.all([
      evidencePost<Schema<'ConcallTranscriptBatchResponse'>>('/v1/concalls/transcripts', { items }),
      input.include_concall_details
        ? evidenceGet<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>('/v1/concalls', {
            symbols,
            quarter: quarters.length === 1 ? quarters[0] : undefined,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'ConcallListItem'> | Schema<'ConcallDetail'>>>>({ data: null }),
      input.include_result_context
        ? evidenceGet<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>('/v1/earnings', {
            symbols,
            quarter: quarters.length === 1 ? quarters[0] : undefined,
            from,
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_1H })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'EarningsListItem'> | Schema<'EarningsDetail'>>>>({ data: null }),
      input.include_result_context
        ? evidenceGet<Schema<'AnnouncementPaginatedResponse'>>('/v1/announcements', {
            symbols,
            from,
            categories: ['Concall', 'Media Release of Financial Results', 'Unaudited Financial Results', 'Investor Presentation'],
            detailed: true,
            limit,
          }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Schema<'AnnouncementPaginatedResponse'>>>({ data: null }),
      input.include_news_context
        ? evidenceGet<Paginated<Schema<'NewsItem'>>>('/v1/news', { symbols, from, limit }, { cacheable: true, ttlMs: TTL_15M })
        : Promise.resolve<EvidenceResult<Paginated<Schema<'NewsItem'>>>>({ data: null }),
    ]);

    return formatToolResult({
      items,
      symbols,
      quarters,
      lookback_days: input.lookback_days,
      transcript_and_audio_artifacts: artifacts,
      concall_details: concalls,
      earnings_context: earnings,
      result_announcements: announcements,
      news_context: news,
      synthesis_guidance: [
        'Use artifact statuses to say whether transcripts/audio are ready, missing, or not found.',
        'Pair transcript/audio URLs with Drishti concall summary, guidance, redflags, and sentiment fields when present.',
        'Do not claim to have read transcript PDFs unless the agent actually fetches or reads those URLs separately.',
      ],
    }, evidenceUrls([artifacts, concalls, earnings, announcements, news], [artifacts.data, concalls.data, news.data]));
  },
});

const FINANCIAL_TOOLS = [
  portfolioBrief,
  peerGroupResearch,
  companyResearch,
  priceVolumeSignalExplainer,
  earningsAnalysis,
  earningsMetricComparison,
  resultSeasonTracker,
  concallSourcePack,
  filingSourcePack,
  ordersCapexTracker,
  corporateActionMonitor,
  governanceMonitor,
  riskMonitor,
  eventCalendar,
  getSymbolMetadata,
  getEarningsIndex,
  getEarnings,
  getEarningsDetail,
  getUpcomingEarnings,
  getConcallsIndex,
  getConcalls,
  getConcallDetail,
  getConcallTranscriptsBatch,
  getUpcomingConcalls,
  getAnnouncements,
  getAlerts,
];

const MARKET_TOOLS = [
  portfolioBrief,
  peerGroupResearch,
  marketPulse,
  priceVolumeSignalExplainer,
  companyResearch,
  resultSeasonTracker,
  concallSourcePack,
  ordersCapexTracker,
  corporateActionMonitor,
  governanceMonitor,
  riskMonitor,
  eventCalendar,
  getNews,
  getAnnouncements,
  getAlerts,
  getSymbolMetadata,
  generateDailySummary,
];

const FILING_TOOLS = [
  earningsAnalysis,
  earningsMetricComparison,
  resultSeasonTracker,
  filingSourcePack,
  companyResearch,
  ordersCapexTracker,
  corporateActionMonitor,
  governanceMonitor,
  riskMonitor,
  eventCalendar,
  getAnnouncements,
  getAnnouncementCategories,
  getAnnouncementAttachments,
  getEarningsIndex,
  getEarnings,
  getEarningsDetail,
  getEarningsAttachments,
  getConcallsIndex,
  getConcalls,
  getConcallDetail,
  getConcallTranscript,
  getConcallTranscriptsBatch,
  getUpcomingEarnings,
  getUpcomingConcalls,
];

function toolMap(tools: StructuredToolInterface[]) {
  return new Map(tools.map((tool) => [tool.name, tool]));
}

function buildRouterPrompt(kind: 'financials' | 'market' | 'filings'): string {
  const shared = `Current date: ${getCurrentDate()}

Drishti covers Indian listed equities. Use NSE/BSE symbols such as RELIANCE, TCS, HDFCBANK. Do not invent US SEC concepts, accession numbers, 10-K, 10-Q, 8-K, insider Form 4, 13F, crypto, or factor-screener data. If the user asks for unavailable US/crypto/screener data, return the closest Drishti-supported context and clearly note the missing public REST capability.`;

  if (kind === 'financials') {
    return `You are a Drishti financial data routing assistant.
${shared}

Route company research to symbol metadata, earnings filings, concalls, announcements, and alerts. For latest quarterly results, prefer drishti_get_earnings with detailed=true. For a specific quarter, use drishti_get_earnings_detail and drishti_get_concall_detail when relevant.`;
  }

  if (kind === 'market') {
    return `You are a Drishti market context routing assistant.
${shared}

Route market/current-context questions to news, announcements, alerts, and daily summaries. The public REST OpenAPI spec available to this integration does not expose live price snapshots or historical OHLCV endpoints; use alerts/news/context for price-move explanations.`;
  }

  return `You are a Drishti Indian exchange filing routing assistant.
${shared}

Use announcements for exchange filings and corporate actions. Use earnings filings for financial results. Use concalls for transcripts/audio/management commentary. Resolve attachment URLs only when the user needs source documents or filing PDFs.`;
}

function createRouterTool(params: {
  name: string;
  description: string;
  model: string;
  tools: StructuredToolInterface[];
  kind: 'financials' | 'market' | 'filings';
}): DynamicStructuredTool {
  const map = toolMap(params.tools);

  return new DynamicStructuredTool({
    name: params.name,
    description: params.description,
    schema: z.object({
      query: z.string().describe('Natural language Drishti research query.'),
    }),
    func: async (input, _runManager, config?: RunnableConfig) => {
      const onProgress = config?.metadata?.onProgress as ((msg: string) => void) | undefined;
      onProgress?.('Routing Drishti request...');

      const { response } = await callLlm(input.query, {
        model: params.model,
        systemPrompt: buildRouterPrompt(params.kind),
        tools: params.tools,
      });

      const toolCalls = (response as AIMessage).tool_calls as ToolCall[];
      if (!toolCalls || toolCalls.length === 0) {
        return formatToolResult({
          error: 'No Drishti tools selected for query',
          note: 'Drishti REST supports Indian symbols, news, announcements, earnings, concalls, alerts, symbol metadata, and daily summaries.',
        });
      }

      onProgress?.(`Fetching ${[...new Set(toolCalls.map((call) => call.name.replace(/^drishti_/, '')))].join(', ')}...`);
      return invokeToolCalls(toolCalls, map);
    },
  });
}

export const GET_FINANCIALS_DESCRIPTION = `
Drishti-native company intelligence for Indian listed equities. Use for symbol metadata, sector/industry/market-cap context, earnings filings and tables, upcoming earnings, conference calls, exchange announcements, and alerts.

This tool does not provide US SEC filings, 13F/institutional holdings, insider Form 4 trades, crypto, full historical statement APIs, or factor screeners. Pass the complete natural-language query once.
`.trim();

export const GET_MARKET_DATA_DESCRIPTION = `
Drishti-native market context for Indian equities. Use for company or market news, corporate announcements, alerts, and watchlist daily summaries.

The public Drishti REST OpenAPI spec used by Max does not expose live quote snapshots or historical OHLCV endpoints. For price-move questions, use alerts/news/announcements context and state when exact price data is unavailable.
`.trim();

export const READ_FILINGS_DESCRIPTION = `
Drishti-native filing reader for Indian exchange disclosures. Use for announcements, corporate actions, earnings filings, conference calls, transcripts/audio URLs, and on-demand attachment URL resolution.

This replaces SEC 10-K/10-Q/8-K reading. Use Indian symbols and Drishti quarter keys like q4_26 when a specific quarter is requested.
`.trim();

export function createGetFinancials(model: string): DynamicStructuredTool {
  return createRouterTool({
    name: 'get_financials',
    description: GET_FINANCIALS_DESCRIPTION,
    model,
    tools: FINANCIAL_TOOLS,
    kind: 'financials',
  });
}

export function createGetMarketData(model: string): DynamicStructuredTool {
  return createRouterTool({
    name: 'get_market_data',
    description: GET_MARKET_DATA_DESCRIPTION,
    model,
    tools: MARKET_TOOLS,
    kind: 'market',
  });
}

export function createReadFilings(model: string): DynamicStructuredTool {
  return createRouterTool({
    name: 'read_filings',
    description: READ_FILINGS_DESCRIPTION,
    model,
    tools: FILING_TOOLS,
    kind: 'filings',
  });
}
